import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Card, Grid, Skeleton, Stack, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  getGetForumTopicsQueryKey,
  useCreateForumTopic,
  useDeleteForumTopic,
  useGetAllTagTypes,
  useGetAllTags,
  useGetForumTopic,
  useGetForumTopicsInfinite,
  useGetMe,
  useGetSiteForum,
  useUpdateForumTopic,
} from '@juno/client-api';
import { CommunityUser, Feed, Forum, ForumTopic, Tag } from '@juno/client-api/model';
import { ANALYTICS_CONFIGURATION } from '@juno/constants';
import {
  ConfirmDeleteDialog,
  Container,
  DefaultBreadcrumbs,
  DefaultSearchSortFilter,
  DrawerHeader,
  FilterSidePanel,
  FiltersApplied,
  RightExpandableDrawer,
  TagsFilter,
} from '@juno/ui';
import {
  MutationAction,
  appendTagFilterUtil,
  createBackendFilters,
  createFilters,
  onMutation,
  removeTagFilterUtil,
  sortFilter,
  tagsFilter,
  useQueryFilters,
  useQuerySearch,
  useSettings,
} from '@juno/utils';
import { useAnalyticsContext } from '../Contexts/AnalyticsContext';
import FeedComponent from '../Feed';
import ForumTopicCard from './ForumTopicCard';
import TopicEditorSection from './TopicEditorSection';
import './style.css';

const PAGE_SIZE = 15;

const DiscussionsSort = {
  NEWEST: '-date_created',
  ALPHABETICAL: 'title',
} as const;

type DiscussionsSort = (typeof DiscussionsSort)[keyof typeof DiscussionsSort];

interface ForumComponentProps {
  siteId: string;
  forum: Forum;
  userCanInteract?: boolean;
  iAmAdmin?: boolean;
  showNewTopicButton?: boolean;
  breadcrumbName?: string;
}
const ForumComponent: React.FC<ForumComponentProps> = ({
  siteId,
  forum,
  userCanInteract = true,
  iAmAdmin = false,
  showNewTopicButton = true,
  breadcrumbName = '',
}) => {
  const filtersConfig = createFilters(
    tagsFilter(),
    sortFilter({ default: [DiscussionsSort.NEWEST] }),
  );

  const { filters, setFilters } = useQueryFilters(filtersConfig);

  const sort = filters.sort?.[0] as DiscussionsSort;
  const setSort = (value: DiscussionsSort) => {
    setFilters((prevFilters) => {
      return {
        ...prevFilters,
        sort: [value],
      };
    });
  };

  const appendTagFilter = (tag: Tag) => {
    setFilters((prevFilters) => appendTagFilterUtil(prevFilters, tag));
  };

  const removeTagFilter = (tag: Tag) => {
    setFilters((prevFilters) => removeTagFilterUtil(prevFilters, tag));
  };

  const { discussionSlug, groupSlug, pageSlug } = useParams();
  const [currentQueryParameters, setSearchParams] = useSearchParams();
  const topicToViewId = currentQueryParameters.get('topic_id');
  const topicRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [createNewTopic, setCreateNewTopic] = useState(false);
  const [showFilters, setShowFilters] = useState(filters.tags?.length > 0);
  const [isSaving, setIsSaving] = useState(false);
  const { localSearch, setLocalSearch, backendSearch } = useQuerySearch({
    toBackend: (value) => {
      return {
        filter_or: {
          title__icontains: value,
          description__icontains: value,
        },
      };
    },
  });

  const [editingTopic, setEditingTopic] = useState<ForumTopic | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [toDeleteTopicId, setToDeleteTopicId] = useState<string | null>(null);
  const { firehoseActivity } = useAnalyticsContext();
  const { user: currentUser, site } = useSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const platformId = site?.platform_id || '';
  const forumBaseUrl = groupSlug
    ? `/${site?.slug}/community/groups/${groupSlug}/discussions`
    : `/${site?.slug}/${pageSlug}`;

  const handleNavigate = (url: string) => {
    /*
      To avoid an infinite re-rendering of the component
      we first check if we're not on the same URL we're trying to redirect to,
      taking into account how the URL is built
    */
    if (url !== `${location.pathname}${location.search}`) {
      navigate(url);
    }
  };

  const { data: me, refetch: refetchMe } = useGetMe(
    platformId,
    {},
    { query: { enabled: !!platformId } },
  );

  const forumTopicsBackendFilters = createBackendFilters(filtersConfig.defs, filters, {
    mergeWith: {
      filter_or: {
        ...backendSearch?.filter_or,
      },
      sort: ['-pinned'],
    },
    override: {
      pagination: {
        limit: PAGE_SIZE,
      },
    },
  });

  const {
    data: forumTopics,
    isLoading: isLoadingTopics,
    refetch: refetchTopics,
    isFetching: isFetchingTopics,
    fetchNextPage,
  } = useGetForumTopicsInfinite(
    siteId,
    forum.id,
    {
      ...forumTopicsBackendFilters,
      offset: 0,
    },
    {
      query: {
        queryKey: [
          getGetForumTopicsQueryKey(siteId, forum.id, {
            ...forumTopicsBackendFilters,
            offset: 0,
          }),
        ],
        getNextPageParam: (lastPage, allPages) => allPages.length || 0,
        initialPageParam: 0,
        enabled: !!forum.id,
      },
    },
  );

  const { data: selectedTopicData, isFetching: selectedTopicFetching } = useGetForumTopic(
    siteId,
    forum.id,
    discussionSlug || '',
    {
      query: { enabled: !!discussionSlug },
    },
  );

  const { ref, inView } = useInView({
    threshold: 0,
    initialInView: false,
  });

  // paginate when the ref comes into view
  useEffect(() => {
    if (inView && !isFetchingTopics) {
      fetchNextPage();
    }
  }, [inView]);

  const topicFoundRef = useRef(false);

  useEffect(() => {
    if (topicToViewId && forumTopics?.pages.length != 0 && !topicFoundRef.current) {
      let topic: ForumTopic | undefined;
      forumTopics?.pages.find((p) =>
        p.find((t) => {
          if (t.feed.id === topicToViewId) {
            topic = t;
          }
        }),
      );

      if (!topic) {
        forumTopics?.pages.find((p) =>
          p.find((t) => {
            if (t.id === topicToViewId) {
              topic = t;
            }
          }),
        );
      }

      if (topic && discussionSlug != topicToViewId && discussionSlug !== topic.id) {
        handleNavigate(`${forumBaseUrl}/${topic?.id}${location.search}`);
      } else if (
        !isFetchingTopics &&
        forumTopics?.pages?.length &&
        forumTopics.pages[forumTopics.pages.length - 1].length >= PAGE_SIZE
      ) {
        // Continue pagination only if not loading and topic is not found
        fetchNextPage();
      }
    }
  }, [isFetchingTopics, topicToViewId, location]);

  useEffect(() => {
    // Reset topicFoundRef when the forum ID or query parameters change
    topicFoundRef.current = false;
  }, [forum.id, currentQueryParameters]);

  const { data: tags, isLoading: isLoadingTags } = useGetAllTags(siteId, {
    order: 'type__value,value',
  });
  const { data: tagTypes, isLoading: isLoadingTagTypes } = useGetAllTagTypes(siteId);

  const tagsFiltersApplied = useMemo(() => {
    if (!filters.tags || !tags) return [];
    const tagIds = filters.tags.map((tagGroup) => tagGroup.tags).flat();
    return tags.filter((tag) => tagIds.includes(tag.id));
  }, [filters.tags, tags]);

  const createTopic = useCreateForumTopic(
    onMutation(MutationAction.CREATE, 'Discussion', refetchTopics),
  );
  const editTopic = useUpdateForumTopic(
    onMutation(MutationAction.UPDATE, 'Discussion', refetchTopics),
  );
  const deleteTopic = useDeleteForumTopic(
    onMutation(MutationAction.DELETE, 'Discussion', refetchTopics),
  );
  const handleSubmitNewTopic = async (newTopic: ForumTopic) => {
    setIsSaving(true);
    try {
      const pageSlug = location.pathname.split('/').pop() || '';
      const topic = await createTopic.mutateAsync({
        siteId,
        forumId: forum.id,
        data: {
          ...newTopic,
          author: currentUser as CommunityUser,
          metadata: {
            page_slug: pageSlug,
          },
        },
      });
      firehoseActivity(
        ANALYTICS_CONFIGURATION.FIREHOSE_OBJECTS.OBJECT_COMMUNITY,
        topic.id,
        forum.id,
        `${currentUser?.id || ''}`,
        ANALYTICS_CONFIGURATION.FIREHOSE_CATEGORIES.CATEGORY_FORUM_TOPIC,
        ANALYTICS_CONFIGURATION.FIREHOSE_ACTIONS.ACTION_CREATE.value,
        topic.title,
        null,
      );
      setIsSaving(false);
      setCreateNewTopic(false);
    } catch (e) {
      console.error(e);
      setIsSaving(false);
      setCreateNewTopic(false);
    }
    // TODO navigate to new topic?
  };

  const handleEditTopic = (topic: ForumTopic) => {
    setEditingTopic(topic);
  };

  const handleSubmitEditTopic = async (topic: ForumTopic) => {
    setIsSaving(true);
    try {
      await editTopic.mutateAsync({
        siteId,
        forumId: forum.id,
        topicId: topic.id,
        data: topic,
      });
      setIsSaving(false);
      setEditingTopic(null);
    } catch (e) {
      console.error(e);
      setIsSaving(false);
      setEditingTopic(null);
    }
  };

  const handlePinUnpin = async (topic: ForumTopic) => {
    try {
      await editTopic.mutateAsync({
        siteId,
        forumId: topic.forum_id,
        topicId: topic.id,
        data: {
          ...topic,
          pinned: !topic.pinned,
          author: topic.author || me,
        },
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async () => {
    setIsSaving(true);
    setDeleteDialogOpen(false);
    try {
      await deleteTopic.mutateAsync({
        siteId,
        forumId: forum.id,
        topicId: toDeleteTopicId || '',
      });
      firehoseActivity(
        ANALYTICS_CONFIGURATION.FIREHOSE_OBJECTS.OBJECT_COMMUNITY,
        toDeleteTopicId || '',
        forum.id,
        `${currentUser?.id || ''}`,
        ANALYTICS_CONFIGURATION.FIREHOSE_CATEGORIES.CATEGORY_FORUM_TOPIC,
        ANALYTICS_CONFIGURATION.FIREHOSE_ACTIONS.ACTION_DELETE.value,
        selectedTopicData?.title || '',
        null,
      );
      setIsSaving(false);
      setCreateNewTopic(false);
      setEditingTopic(null);
    } catch (e) {
      console.error(e);
      setIsSaving(false);
      setCreateNewTopic(false);
      setEditingTopic(null);
    }
  };

  const hasReportedView = useRef(false);
  useEffect(() => {
    // Record forum view analytics
    if (forum && me && !hasReportedView.current) {
      hasReportedView.current = true;
      firehoseActivity(
        ANALYTICS_CONFIGURATION.FIREHOSE_OBJECTS.OBJECT_COMMUNITY,
        forum?.id || '',
        null,
        `${me?.id || ''}`,
        ANALYTICS_CONFIGURATION.FIREHOSE_CATEGORIES.CATEGORY_FORUM,
        ANALYTICS_CONFIGURATION.FIREHOSE_ACTIONS.ACTION_VIEW.value,
        '',
        null,
      );
    }
  }, [firehoseActivity, forum, me]);

  return (
    <Box>
      {!discussionSlug && (
        <>
          {!userCanInteract && (
            <Alert severity='warning'>
              {'You must be a member of this group to create, follow or view topics.'}
            </Alert>
          )}
          <DefaultSearchSortFilter
            showButton={showNewTopicButton}
            buttonDisabled={!userCanInteract}
            buttonText={'New Discussion'}
            sort={sort}
            sortOptions={[
              { value: DiscussionsSort.ALPHABETICAL, label: 'Alphabetical' },
              { value: DiscussionsSort.NEWEST, label: 'Newest' },
            ]}
            onClickButton={() => setCreateNewTopic(true)}
            search={localSearch}
            setSearch={(value) => setLocalSearch(value)}
            setSort={(value) => {
              setSort(value as DiscussionsSort);
            }}
            onClickFilter={() => setShowFilters((prev) => !prev)}
            isFilterSelected={showFilters}
          />
          {tagsFiltersApplied.length > 0 && (
            <Grid item xs={12}>
              <FiltersApplied
                tagFilters={tagsFiltersApplied}
                disableAll={isFetchingTopics}
                removeTagFilter={removeTagFilter}
              />
            </Grid>
          )}
          {!isLoadingTopics &&
            !tagsFiltersApplied.length &&
            forumTopics?.pages?.[0]?.length === 0 &&
            !backendSearch &&
            userCanInteract && (
              <Box
                display='flex'
                flexDirection='column'
                alignItems='center'
                justifyContent='center'
                minHeight='100px'
              >
                <Typography variant='body2'>Be the first to start a conversation.</Typography>
              </Box>
            )}

          <Grid container spacing={2} mt={2}>
            {showFilters && (
              <Grid item sm={3} xs={12}>
                <FilterSidePanel>
                  <TagsFilter
                    tags={tags || []}
                    tagTypes={tagTypes || []}
                    selectedFilters={tagsFiltersApplied}
                    appendFilter={appendTagFilter}
                    removeFilter={removeTagFilter}
                    isLoadingTagTypes={isLoadingTagTypes}
                    isLoadingTags={isLoadingTags}
                    disabled={false}
                    totalFilterApplied={tagsFiltersApplied.length}
                  />
                </FilterSidePanel>
              </Grid>
            )}

            {tagsFiltersApplied.length > 0 && forumTopics?.pages?.[0]?.length === 0 && (
              <Grid item sm={showFilters ? 9 : 12} xs={12}>
                <Box
                  display='flex'
                  flexDirection='column'
                  alignItems='center'
                  justifyContent='center'
                  minHeight='100px'
                >
                  <Typography variant='body2'>No results found.</Typography>
                </Box>
              </Grid>
            )}

            <Grid item sm={showFilters ? 9 : 12} xs={12}>
              {forumTopics && forumTopics.pages.length > 0 && (
                <Stack spacing={1}>
                  {forumTopics?.pages.map((page, i) =>
                    page.map((topic) => {
                      return (
                        <Box
                          key={`topic__${topic.id}`}
                          ref={(el) => {
                            topicRefs.current[topic.id] = el as HTMLDivElement;
                          }}
                        >
                          <motion.div
                            animate={{
                              scale: topicToViewId === topic.id ? [1, 1.1, 1] : 1,
                            }}
                          >
                            <ForumTopicCard
                              userCanInteract={userCanInteract}
                              topic={topic}
                              me={me}
                              refetchMe={refetchMe}
                              siteId={siteId}
                              refetchTopics={refetchTopics}
                              topicsFetching={isFetchingTopics}
                              iAmAdmin={iAmAdmin}
                              handleEditTopic={handleEditTopic}
                              setDeleteDialogOpen={setDeleteDialogOpen}
                              setToDeleteTopicId={setToDeleteTopicId}
                              handleTagFilter={(tag) => {
                                const exists = tagsFiltersApplied.find((t) => t.id === tag.id);
                                if (exists) {
                                  removeTagFilter(tag);
                                } else {
                                  appendTagFilter(tag);
                                }
                              }}
                              handlePinUnpin={handlePinUnpin}
                            />
                          </motion.div>
                        </Box>
                      );
                    }),
                  )}
                  {isLoadingTopics && (
                    <>
                      <DiscussionSkeleton />
                      <DiscussionSkeleton />
                    </>
                  )}
                  {isFetchingTopics && <DiscussionSkeleton />}
                  {!isFetchingTopics &&
                  forumTopics?.pages[forumTopics.pages.length - 1].length !== 0 ? (
                    <Box ref={ref} className='pagination-element' sx={{ height: 20 }}></Box>
                  ) : (
                    <Box sx={{ height: 20 }} />
                  )}
                </Stack>
              )}
              <RightExpandableDrawer
                open={!!editingTopic}
                onClose={() => {
                  setEditingTopic(null);
                }}
              >
                <DrawerHeader title={'Edit Discussion'} />
                <TopicEditorSection
                  sidebarTitle='Edit Discussion'
                  handleSubmit={handleSubmitNewTopic}
                  handleCancel={() => {
                    setCreateNewTopic(false);
                    setEditingTopic(null);
                  }}
                  isSaving={isSaving}
                  editingTopic={editingTopic}
                  handleEditSubmit={handleSubmitEditTopic}
                />
              </RightExpandableDrawer>

              <RightExpandableDrawer open={createNewTopic} onClose={() => setCreateNewTopic(false)}>
                <DrawerHeader title={'Create a Discussion'} />
                <TopicEditorSection
                  sidebarTitle='Create Discussion'
                  handleSubmit={handleSubmitNewTopic}
                  handleCancel={() => {
                    setCreateNewTopic(false);
                  }}
                  isSaving={isSaving}
                  editingTopic={null}
                  handleEditSubmit={handleSubmitEditTopic}
                />
              </RightExpandableDrawer>
            </Grid>
          </Grid>
        </>
      )}
      {discussionSlug && !selectedTopicFetching && (
        <Box>
          <DefaultBreadcrumbs
            items={[
              {
                text: `${breadcrumbName} Discussions`,
                onClick: () => {
                  setSearchParams({});
                  handleNavigate(forumBaseUrl);
                  refetchTopics();
                },
                isLink: true,
              },
              {
                text: selectedTopicData?.title || '',
              },
            ]}
          />
          <FeedComponent
            feed={selectedTopicData?.feed || ({} as Feed)}
            userCanInteract={userCanInteract}
            iAmAdmin={iAmAdmin}
            isInForum={true}
          />
        </Box>
      )}
      {deleteDialogOpen && toDeleteTopicId && (
        <ConfirmDeleteDialog
          handleClose={() => setDeleteDialogOpen(false)}
          handleDelete={() => {
            handleDelete();
          }}
          title={`Delete Discussion?`}
          message={
            <Typography variant='subtitle1'>
              Are you sure you want to delete this discussion?
            </Typography>
          }
          subText={
            <Typography color='error' variant='body2' sx={{ mt: 1 }}>
              This includes any posts, comments, and replies within the discussion.
            </Typography>
          }
        />
      )}
    </Box>
  );
};
export default ForumComponent;

interface GlobalForumProps {
  siteId: string;
  title: string;
  allowMemberPosts: boolean;
}

export const GlobalForum: React.FC<GlobalForumProps> = ({ siteId, title, allowMemberPosts }) => {
  const { data: forum } = useGetSiteForum(siteId);
  const { user } = useSettings();
  const iAmAdmin = user?.role === 1 || false;
  if (!forum) {
    return null;
  }
  return (
    <Container p={2}>
      {title && (
        <Typography variant='h5' sx={{ ml: 1, mb: 2 }}>
          {title}
        </Typography>
      )}
      <ForumComponent
        siteId={siteId}
        forum={forum}
        iAmAdmin={iAmAdmin}
        showNewTopicButton={allowMemberPosts || iAmAdmin}
      />
    </Container>
  );
};

const DiscussionSkeleton = () => {
  return (
    <Card sx={{ height: 260, mt: -1, p: 2 }}>
      <Stack direction='row' alignItems={'center'}>
        <Skeleton variant='circular' height={50} width={50} />
        <Skeleton sx={{ ml: 2 }} width={200} height={30} />
        <Skeleton sx={{ ml: 'auto' }} width={80} height={20} />
      </Stack>

      <Skeleton width={300} height={40} sx={{ mt: 0.5, mb: 0 }} />
      <Skeleton width={'100%'} height={120} sx={{ mt: -2 }} />
      <Stack direction='row' alignItems={'center'} justifyContent={'flex-end'}>
        <Skeleton variant='circular' height={22} width={22} />
        <Skeleton sx={{ ml: 1 }} width={50} height={30} />
        <Skeleton variant='circular' height={22} width={22} sx={{ ml: 6 }} />
        <Skeleton sx={{ ml: 1, mr: 5 }} width={80} height={30} />
      </Stack>
    </Card>
  );
};
